"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Le retour d'action de la bibliothèque : « Retiré de mes morceaux. » et son
 * « Annuler », « Lien copié. » et ce que le lien permet.
 *
 * # Ce qu'un retour d'action dit, et ce qu'il ne dit pas
 *
 * Il dit la **conséquence**, pas la réussite. « Lien copié » est ce que la
 * personne voit déjà ; ce qu'elle ne sait pas, c'est que toute personne avec ce
 * lien pourra ouvrir le morceau. D'où la seconde ligne, facultative, qui porte
 * la conséquence quand elle n'est pas évidente.
 *
 * # Pourquoi un contexte
 *
 * Les gestes partent de partout — une carte de grille, une bande, une ligne de
 * playlist, une fiche, l'atterrissage d'un lien — et le retour doit rester
 * unique et posé au même endroit. Un état local par carte aurait empilé des
 * bandeaux, ou pire, aurait disparu avec la carte : c'est exactement le cas du
 * retrait, où la ligne qui déclenche le message cesse d'exister au même
 * instant. Le message doit donc survivre à son émetteur.
 *
 * Monté par `LibraryShell`, donc disponible sur toute la bibliothèque.
 */

export type LibraryFeedbackAction = {
  readonly label: string;
  readonly onAct: () => void;
};

export type LibraryFeedback = {
  readonly message: string;
  /** La conséquence, quand elle n'est pas évidente. */
  readonly detail?: string;
  /** Un seul geste, réversible ou de suivi : « Annuler », « Voir ». */
  readonly action?: LibraryFeedbackAction;
  /** Le message dit un échec : il se teint, il ne disparaît pas plus vite. */
  readonly failed?: boolean;
};

type LibraryFeedbackContextValue = {
  notify: (feedback: LibraryFeedback) => void;
};

const LibraryFeedbackContext = createContext<LibraryFeedbackContextValue | null>(
  null,
);

/**
 * Cinq secondes, la durée que le chapitre « Annuler » suppose.
 *
 * Assez pour lire une phrase et revenir sur un geste, assez court pour ne pas
 * masquer le bas de l'écran pendant qu'on continue à ranger.
 */
const VISIBLE_MS = 5000;

export function LibraryFeedbackProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<LibraryFeedback | null>(null);
  const timer = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const notify = useCallback(
    (feedback: LibraryFeedback) => {
      clear();
      setCurrent(feedback);
      timer.current = window.setTimeout(() => setCurrent(null), VISIBLE_MS);
    },
    [clear],
  );

  useEffect(() => clear, [clear]);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <LibraryFeedbackContext.Provider value={value}>
      {children}
      {current !== null && (
        <FeedbackToast
          feedback={current}
          onClose={() => {
            clear();
            setCurrent(null);
          }}
        />
      )}
    </LibraryFeedbackContext.Provider>
  );
}

export function useLibraryFeedback(): LibraryFeedbackContextValue {
  const context = useContext(LibraryFeedbackContext);

  if (context === null) {
    throw new Error(
      "useLibraryFeedback doit être appelé sous un LibraryFeedbackProvider",
    );
  }

  return context;
}

/**
 * En bas et centré, jamais en haut : le geste part d'une carte ou d'une ligne,
 * c'est-à-dire du corps de la page. Un bandeau qui apparaîtrait sous le header
 * obligerait à quitter des yeux l'endroit où l'on vient de cliquer.
 *
 * `inset-safe` n'existe qu'en `position: fixed` sur ce produit : la marge basse
 * suit la barre d'accueil des téléphones, sinon le bouton « Annuler » tombe
 * dessous et n'est plus atteignable.
 */
function FeedbackToast({
  feedback,
  onClose,
}: {
  feedback: LibraryFeedback;
  onClose: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed left-1/2 z-[70] w-[calc(100vw-32px)] max-w-[460px] -translate-x-1/2 rounded-xl px-4 py-3"
      style={{
        bottom: "calc(24px + var(--safe-bottom, 0px))",
        backgroundColor: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-strong)",
        boxShadow: "var(--shadow-popin)",
      }}
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <p
            className="text-[13.5px] leading-snug"
            style={{
              color: feedback.failed
                ? "var(--color-err)"
                : "var(--color-text-primary)",
            }}
          >
            {feedback.message}
          </p>
          {feedback.detail !== undefined && (
            <p className="mt-[3px] text-[12px] leading-[1.45] text-text-soft">
              {feedback.detail}
            </p>
          )}
        </div>

        {feedback.action !== undefined && (
          <button
            type="button"
            onClick={() => {
              feedback.action?.onAct();
              onClose();
            }}
            className="-my-1 shrink-0 cursor-pointer rounded-lg px-2 py-1.5 text-[13px] font-semibold text-accent-bright transition-colors duration-150 hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {feedback.action.label}
          </button>
        )}
      </div>
    </div>
  );
}
